/**
 * Cross-Sell Recommendations with Swiper
 * Stale-while-revalidate: shows cached slides instantly, then refreshes in background.
 * Avoids extra /cart.js fetch by tracking product_id via cart:change events.
 */

const _CS_CACHE_PREFIX = 'cross-sell-v1-';
const _CS_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

let _csPid = null;
try { _csPid = localStorage.getItem('cross-sell-pid'); } catch (e) {}

// Signature of every product_id currently in the cart. Folded into the cache key so that
// adding/removing an item invalidates cached slides — the server-rendered section excludes
// in-cart products, so stale (pre-add) slides must not be served after a cart mutation.
let _csCartSig = '';
try { _csCartSig = localStorage.getItem('cross-sell-cartsig') || ''; } catch (e) {}

function _csComputeCartSig(items) {
  if (!Array.isArray(items) || items.length === 0) return '';
  return Array.from(new Set(items.map((i) => i.product_id)))
    .sort((a, b) => a - b)
    .join('-');
}

document.addEventListener('cart:change', (event) => {
  const items = event.detail?.cart?.items;
  if (items?.length > 0) {
    _csPid = String(items[0].product_id);
    _csCartSig = _csComputeCartSig(items);
    try {
      localStorage.setItem('cross-sell-pid', _csPid);
      localStorage.setItem('cross-sell-cartsig', _csCartSig);
    } catch (e) {}
  } else if (items !== undefined && items.length === 0) {
    _csPid = null;
    _csCartSig = '';
    try {
      localStorage.removeItem('cross-sell-pid');
      localStorage.removeItem('cross-sell-cartsig');
    } catch (e) {}
  }
});

function _csCacheKey(productId, cartSig) {
  return _CS_CACHE_PREFIX + productId + '|' + (cartSig || '');
}

function _csGetCache(productId, cartSig) {
  try {
    const raw = localStorage.getItem(_csCacheKey(productId, cartSig));
    if (!raw) return null;
    const { slides, ts } = JSON.parse(raw);
    if (Date.now() - ts > _CS_CACHE_TTL) {
      localStorage.removeItem(_csCacheKey(productId, cartSig));
      return null;
    }
    return slides; // string[]
  } catch (e) {
    return null;
  }
}

function _csSetCache(productId, cartSig, slides) {
  try {
    localStorage.setItem(
      _csCacheKey(productId, cartSig),
      JSON.stringify({ slides, ts: Date.now() })
    );
  } catch (e) {}
}

if (!customElements.get('cross-sell-recommendations')) {
  customElements.define('cross-sell-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.limit = this.getAttribute('data-limit') || 10;
      this.sectionId = 'api-cross-sell-recommendations';
      this.swiperInstance = null;
      this._loading = false;
      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }
    }

    async loadRecommendations() {
      if (this._loading) return;
      this._loading = true;

      try {
        let productId = _csPid;
        let cartSig = _csCartSig;

        if (!productId) {
          const cart = await fetch('/cart.js').then(r => r.json());
          if (!cart.items || cart.items.length === 0) {
            this.style.display = 'none';
            return;
          }
          productId = String(cart.items[0].product_id);
          cartSig = _csComputeCartSig(cart.items);
          _csPid = productId;
          _csCartSig = cartSig;
          try {
            localStorage.setItem('cross-sell-pid', productId);
            localStorage.setItem('cross-sell-cartsig', cartSig);
          } catch (e) {}
        }

        const cachedSlides = _csGetCache(productId, cartSig);
        let renderedFromCache = false;

        if (cachedSlides?.length > 0) {
          this._renderSlides(cachedSlides);
          renderedFromCache = true;
        }

        const url = `/recommendations/products?product_id=${productId}&intent=related&limit=${this.limit}&section_id=${this.sectionId}`;
        const response = await fetch(url);

        if (!response.ok) {
          if (!renderedFromCache) this.style.display = 'none';
          return;
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = doc.querySelector('.cross-sell-recommendations');

        if (!container || container.children.length === 0) {
          if (!renderedFromCache) this.style.display = 'none';
          return;
        }

        const freshSlides = Array.from(container.children).map(el => el.outerHTML);

        _csSetCache(productId, cartSig, freshSlides);

        const cachedStr = cachedSlides ? cachedSlides.join('') : null;
        const freshStr = freshSlides.join('');

        if (!renderedFromCache || cachedStr !== freshStr) {
          this._renderSlides(freshSlides);
        }

      } catch (error) {
        console.error('[CrossSell] Error:', error);
        if (!this.querySelector('.swiper-slide')) {
          this.style.display = 'none';
        }
      } finally {
        this._loading = false;
      }
    }

    _renderSlides(slides) {
      const wrapper = this.querySelector('.swiper-wrapper');
      if (!wrapper || slides.length === 0) {
        this.style.display = 'none';
        return;
      }

      wrapper.innerHTML = '';
      slides.forEach(slideHtml => {
        const slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.innerHTML = slideHtml;
        wrapper.appendChild(slide);
      });

      this.style.display = '';
      this.initSwiper();
    }

    initSwiper() {
      if (typeof Swiper === 'undefined') {
        console.warn('[CrossSell] Swiper not loaded');
        return;
      }

      const swiperEl = this.querySelector('.cross-sell-swiper');
      if (!swiperEl) return;

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
      }

      // Nav + pagination live in the slider header (sibling of this element), so scope
      // the lookups to the enclosing .cross-sell-slider rather than this custom element.
      const sliderScope = this.closest('.cross-sell-slider') || this;
      const paginationEl = sliderScope.querySelector('.cross-sell-swiper__pagination');
      const nextEl = sliderScope.querySelector('.cross-sell-swiper__button-next');
      const prevEl = sliderScope.querySelector('.cross-sell-swiper__button-prev');

      this.swiperInstance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 12,
        grabCursor: true,
        watchOverflow: true,
        navigation: {
          nextEl: nextEl,
          prevEl: prevEl,
        },
        pagination: paginationEl ? {
          el: paginationEl,
          clickable: true,
        } : false,
      });
    }
  });
}

// Initialize static cross-sell Swiper sliders
function initStaticCrossSellSwipers() {
  if (typeof Swiper === 'undefined') {
    setTimeout(initStaticCrossSellSwipers, 100);
    return;
  }

  document.querySelectorAll('.cross-sell-swiper:not(.swiper-initialized)').forEach(swiperEl => {
    // Skip if inside cross-sell-recommendations custom element
    if (swiperEl.closest('cross-sell-recommendations')) return;

    // Nav + pagination live in the slider header (sibling of the swiper container),
    // so scope the lookups to the enclosing .cross-sell-slider.
    const sliderScope = swiperEl.closest('.cross-sell-slider') || document;
    const paginationEl = sliderScope.querySelector('.cross-sell-swiper__pagination');
    const nextEl = sliderScope.querySelector('.cross-sell-swiper__button-next');
    const prevEl = sliderScope.querySelector('.cross-sell-swiper__button-prev');

    new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 12,
      grabCursor: true,
      watchOverflow: true,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: paginationEl ? {
        el: paginationEl,
        clickable: true,
      } : false,
    });
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initStaticCrossSellSwipers);

// Re-initialize when cart drawer is refreshed
document.addEventListener('cart-drawer:refreshed', () => {
  // Small delay to ensure DOM is updated
  setTimeout(initStaticCrossSellSwipers, 50);
});
